import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { moodsApi } from '@/lib/api';
import { toast } from 'sonner';

const MOOD_OPTIONS = [
  { level: 1, labelKey: 'mood_awful', color: '#ef4444', bgColor: 'rgba(239,68,68,0.15)' },
  { level: 2, labelKey: 'mood_down', color: '#f97316', bgColor: 'rgba(249,115,22,0.15)' },
  { level: 3, labelKey: 'mood_okay', color: '#eab308', bgColor: 'rgba(234,179,8,0.15)' },
  { level: 4, labelKey: 'mood_good', color: '#22c55e', bgColor: 'rgba(34,197,94,0.15)' },
  { level: 5, labelKey: 'mood_great', color: '#00d4ff', bgColor: 'rgba(0,212,255,0.15)' },
];

const MoodFace = ({ level, color, size = 48, selected }) => {
  const s = size;
  const cx = s / 2;
  const r = s / 2 - 2;
  const eyeY = cx - r * 0.15;
  const eyeR = r * 0.08;
  const mouthY = cx + r * 0.2;

  const getMouth = () => {
    switch (level) {
      case 1:
        return <path d={`M ${cx - r * 0.3} ${mouthY + r * 0.15} Q ${cx} ${mouthY - r * 0.2} ${cx + r * 0.3} ${mouthY + r * 0.15}`} stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />;
      case 2:
        return <path d={`M ${cx - r * 0.25} ${mouthY + r * 0.08} Q ${cx} ${mouthY - r * 0.05} ${cx + r * 0.25} ${mouthY + r * 0.08}`} stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />;
      case 3:
        return <line x1={cx - r * 0.2} y1={mouthY} x2={cx + r * 0.2} y2={mouthY} stroke={color} strokeWidth="2" strokeLinecap="round" />;
      case 4:
        return <path d={`M ${cx - r * 0.25} ${mouthY - r * 0.05} Q ${cx} ${mouthY + r * 0.15} ${cx + r * 0.25} ${mouthY - r * 0.05}`} stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />;
      case 5:
        return <path d={`M ${cx - r * 0.3} ${mouthY - r * 0.1} Q ${cx} ${mouthY + r * 0.3} ${cx + r * 0.3} ${mouthY - r * 0.1}`} stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />;
      default:
        return null;
    }
  };

  return (
    <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
      <circle cx={cx} cy={cx} r={r} fill="none" stroke={color} strokeWidth={selected ? 2.5 : 1.5} opacity={selected ? 1 : 0.7} />
      <circle cx={cx - r * 0.25} cy={eyeY} r={eyeR} fill={color} />
      <circle cx={cx + r * 0.25} cy={eyeY} r={eyeR} fill={color} />
      {getMouth()}
    </svg>
  );
};

export default function MoodCheckinModal({ isOpen, onClose, onComplete }) {
  const [selectedMood, setSelectedMood] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [step, setStep] = useState('select');
  const { t } = useLanguage();

  const handleSubmit = async () => {
    if (!selectedMood) return;
    setSubmitting(true);
    try {
      const res = await moodsApi.create({
        mood_level: selectedMood.level,
        mood_label: t(selectedMood.labelKey),
        note
      });
      setAiResult(res.data);
      setStep('result');
      toast.success('Mood logged!');
    } catch (err) {
      console.error('Mood checkin error:', err);
      toast.error('Failed to save mood');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    sessionStorage.setItem('mood_checked', '1');
    setSelectedMood(null);
    setNote('');
    setAiResult(null);
    setStep('select');
    onComplete?.();
    onClose();
  };

  const handleClose = () => {
    sessionStorage.setItem('mood_checked', '1');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={(e) => e.target === e.currentTarget && step === 'select' && handleClose()}
        data-testid="mood-checkin-overlay"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-full max-w-md bg-navy-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
          data-testid="mood-checkin-modal"
        >
          {step === 'select' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-heading text-white">{t('mood_checkin_title')}</h2>
                  <p className="text-slate-400 text-sm mt-1">{t('mood_checkin_subtitle')}</p>
                </div>
                <button onClick={handleClose} className="text-slate-500 hover:text-white transition-colors" data-testid="mood-modal-close">
                  <X size={20} />
                </button>
              </div>

              <div className="flex justify-between items-center gap-2 mb-6">
                {MOOD_OPTIONS.map((mood) => (
                  <motion.button
                    key={mood.level}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedMood(mood)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all duration-200 ${
                      selectedMood?.level === mood.level
                        ? 'ring-2 ring-offset-2 ring-offset-navy-900'
                        : 'hover:bg-white/5'
                    }`}
                    style={{
                      backgroundColor: selectedMood?.level === mood.level ? mood.bgColor : 'transparent',
                      ringColor: selectedMood?.level === mood.level ? mood.color : 'transparent',
                      '--tw-ring-color': mood.color,
                    }}
                    data-testid={`mood-option-${mood.level}`}
                  >
                    <MoodFace level={mood.level} color={mood.color} size={44} selected={selectedMood?.level === mood.level} />
                    <span className="text-xs font-medium" style={{ color: selectedMood?.level === mood.level ? mood.color : '#94a3b8' }}>
                      {t(mood.labelKey)}
                    </span>
                  </motion.button>
                ))}
              </div>

              <div className="mb-5">
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t('mood_note_placeholder')}
                  className="w-full bg-navy-950 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-slate-600 focus:border-cyan focus:ring-1 focus:ring-cyan transition-all resize-none"
                  rows={3}
                  data-testid="mood-note-input"
                />
              </div>

              <Button
                onClick={handleSubmit}
                disabled={!selectedMood || submitting}
                className="w-full bg-cyan text-navy-950 hover:bg-cyan/90 rounded-xl font-semibold h-11 disabled:opacity-40"
                data-testid="mood-submit-btn"
              >
                {submitting ? (
                  <span className="flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> {t('mood_analyzing')}</span>
                ) : (
                  <span className="flex items-center gap-2"><Sparkles size={18} /> {t('mood_submit')}</span>
                )}
              </Button>
            </div>
          )}

          {step === 'result' && aiResult && (
            <div className="p-6">
              <div className="text-center mb-5">
                <MoodFace level={selectedMood.level} color={selectedMood.color} size={56} selected />
                <h2 className="text-lg font-heading text-white mt-3">{t('mood_logged')}</h2>
                <span className="text-sm font-medium px-3 py-1 rounded-full inline-block mt-1" style={{ color: selectedMood.color, backgroundColor: selectedMood.bgColor }}>
                  {t(selectedMood.labelKey)}
                </span>
              </div>

              {aiResult.ai_analysis && (
                <div className="bg-navy-950/80 border border-white/5 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="text-gold" size={16} />
                    <span className="text-gold text-sm font-medium">{t('mood_ai_insight')}</span>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed" data-testid="mood-ai-analysis">{aiResult.ai_analysis}</p>
                </div>
              )}

              {aiResult.ai_tips?.length > 0 && (
                <div className="space-y-2 mb-5">
                  <span className="text-slate-400 text-xs font-medium uppercase tracking-wider">{t('mood_suggestions')}</span>
                  {aiResult.ai_tips.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <span className="text-cyan mt-0.5 flex-shrink-0">&#x2022;</span>
                      <span>{tip}</span>
                    </div>
                  ))}
                </div>
              )}

              <Button
                onClick={handleDone}
                className="w-full bg-cyan text-navy-950 hover:bg-cyan/90 rounded-xl font-semibold h-11"
                data-testid="mood-done-btn"
              >
                {t('mood_continue')}
              </Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
